import {useEffect, useRef, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button, Spinner} from 'react-bootstrap'
import {CheckCircle, XCircle} from 'react-bootstrap-icons'
import UnauthenticatedLayout from '../../components/layout/UnauthenticatedLayout'
import {authService} from '../../services/authService'

type VerificationState = 'verifying' | 'success' | 'error'

interface VerificationResponse {
    success: boolean
    message?: string
    needsProfileCompletion?: boolean
}

interface ErrorResponse {
    message?: string
}

const EmailVerificationPage: React.FC = () => {
    const {token} = useParams<{token: string}>()
    const navigate = useNavigate()
    const {t} = useTranslation()
    const [verificationState, setVerificationState] = useState<VerificationState>('verifying')
    const [message, setMessage] = useState('')
    const hasVerifiedRef = useRef(false)

    useEffect(() => {
        if (hasVerifiedRef.current) return
        hasVerifiedRef.current = true

        const verifyEmail = async () => {
            if (!token) {
                setVerificationState('error')
                setMessage('No verification token provided')
                return
            }

            try {
                const data = await authService.verifyEmail(token) as unknown as VerificationResponse

                if (data.success) {
                    setVerificationState('success')
                    setMessage(data.message || 'Email verified successfully')
                    setTimeout(() => {
                        if (data.needsProfileCompletion) {
                            navigate('/profile/complete')
                        } else {
                            navigate('/login')
                        }
                    }, 3000)
                } else {
                    setVerificationState('error')
                    setMessage(data.message || 'Email verification failed')
                }
            } catch (error) {
                setVerificationState('error')
                const err = error as {response?: {data?: ErrorResponse}}
                setMessage(
                    err.response?.data?.message ||
                    'Invalid or expired token'
                )
            }
        }

        verifyEmail()
    }, [token, navigate])

    return (
        <UnauthenticatedLayout>
            <div className="text-center">
                {verificationState === 'verifying' && (
                    <>
                        <Spinner animation="border" variant="primary" className="mb-3"/>
                        <h3>{t('auth:verifying_email', 'Verifying your email...')}</h3>
                        <p>
                            {t('auth:please_wait', 'Please wait while we verify your email address.')}
                        </p>
                    </>
                )}

                {verificationState === 'success' && (
                    <>
                        <CheckCircle size={64} className="text-white mb-3"/>
                        <h3>{t('auth:verification_success', 'Email Verified!')}</h3>
                        <p data-testid="success-message">{message}</p>
                        <p>
                            {t('auth:redirecting', 'Redirecting you in a moment...')}
                        </p>
                    </>
                )}

                {verificationState === 'error' && (
                    <>
                        <XCircle size={64} className="text-danger mb-3"/>
                        <h3>{t('auth:verification_failed_title', 'Verification Failed')}</h3>
                        <p data-testid="error-message">{message}</p>
                        <Button
                            variant="primary"
                            className="mt-3"
                            onClick={() => navigate('/login')}
                        >
                            {t('auth:go_to_login', 'Go to Login')}
                        </Button>
                    </>
                )}
            </div>
        </UnauthenticatedLayout>
    )
}

export default EmailVerificationPage
